/** Shared chat list/thread presentation helpers — parity with iOS `MemberChatLogic`. */

export const CHAT_CONNECTED_COPY =
  'You are now connected and can start discussing your goals. Just say hello!';

export function isSystemChatMessage(message: { type?: string | null; content?: string | null }): boolean {
  const content = message.content ?? '';
  return (
    message.type === 'system' ||
    content.includes('You are now connected and can start discussing') ||
    content.includes("You're connected!")
  );
}

export function parseChatDate(iso: string): Date | null {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatChatListTime(iso: string, now: Date = new Date()): string {
  const date = parseChatDate(iso);
  if (!date) return '';
  if (isSameDay(date, now)) return shortTime(date);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameDay(date, yesterday)) return 'Yesterday';
  const dayDiff = startOfDay(now).getTime() - startOfDay(date).getTime();
  const days = Math.round(dayDiff / 86_400_000);
  if (days > 0 && days < 7) {
    return date.toLocaleDateString(undefined, { weekday: 'short' });
  }
  if (days >= 7) return `${Math.max(1, Math.floor(days / 7))}w`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function formatChatThreadTime(iso: string): string {
  const date = parseChatDate(iso);
  if (!date) return '';
  return shortTime(date);
}

export function formatChatDaySeparator(iso: string, now: Date = new Date()): string {
  const date = parseChatDate(iso);
  if (!date) return '';
  if (isSameDay(date, now)) return 'Today';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameDay(date, yesterday)) return 'Yesterday';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function isSameChatDay(lhsISO: string, rhsISO: string): boolean {
  const lhs = parseChatDate(lhsISO);
  const rhs = parseChatDate(rhsISO);
  if (!lhs || !rhs) return false;
  return isSameDay(lhs, rhs);
}

export function groupedChatSearch<T extends { displayName: string; preview: string }>(
  items: T[],
  query: string,
): { people: T[]; messages: T[] } {
  const needle = query.trim();
  if (!needle) return { people: items, messages: [] };
  const lower = needle.toLowerCase();
  return {
    people: items.filter((item) => item.displayName.toLowerCase().includes(lower)),
    messages: items.filter((item) => item.preview.toLowerCase().includes(lower)),
  };
}

export function matchRanges(query: string, text: string): Array<[number, number]> {
  const needle = query.trim();
  if (!needle) return [];
  const ranges: Array<[number, number]> = [];
  const haystack = text.toLowerCase();
  const find = needle.toLowerCase();
  let from = 0;
  while (from < haystack.length) {
    const index = haystack.indexOf(find, from);
    if (index < 0) break;
    ranges.push([index, index + needle.length]);
    from = index + needle.length;
  }
  return ranges;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function shortTime(date: Date): string {
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
